import { FormattedMessage } from 'react-intl';

import { modals } from '@mantine/modals';

import type { RootState } from '../../store/root-state';
import { selectPaymentMethodsHasActiveShipmentInvoicePayment } from '../../store/payment-methods/payment-methods.selectors';
import Global from '../../store/global-selectors';
import { UpsInvoicedNotificationModal } from '../components/ups-invoiced-notification-modal';
import { messages } from '../components/ups-invoiced-notification-modal/messages';
import { getMaxZ } from '../../ui-components/hooks/services/use-max-z';
import type { LabelPurchaseGate } from '../label-purchase-gate.types';

const isAnySellerLabelProviderUpsInvoiced = (state: RootState, sellerLabelProviderIds: string[]): boolean =>
  sellerLabelProviderIds.some((sellerLabelProviderId) => Global.selectSellerLabelProviderIsUpsInvoiced(state, { sellerLabelProviderId }));

// Sentinel fulfillment plan ids (e.g. CUSTOMERS_CREATE_LABEL, RATE_BROWSER) have no entities row,
// so the selected provider is read from the CSW shipping configuration instead.
const resolveSellerLabelProviderId = (state: RootState, fulfillmentPlanId: string): string | undefined =>
  Global.selectEntitiesFulfillmentPlanSellerLabelProviderID(state, { fulfillmentPlanId }) ??
  Global.selectCSWShippingConfiguration(state, { fulfillmentPlanId })?.sellerLabelProviderId;

const isAnyFulfillmentPlanIsUpsInvoiced = (state: RootState, fulfillmentPlanIds: string[]): boolean =>
  fulfillmentPlanIds.some((fulfillmentPlanId) => {
    const sellerLabelProviderId = resolveSellerLabelProviderId(state, fulfillmentPlanId);
    return Global.selectSellerLabelProviderIsUpsInvoiced(state, { sellerLabelProviderId });
  });

export const upsInvoicedGate: LabelPurchaseGate = {
  id: 'ups-invoiced',
  shouldApply: ({ state, fulfillmentPlanIds, sellerLabelProviderIds }) => {
    const isUpsInvoiced = sellerLabelProviderIds?.length
      ? isAnySellerLabelProviderUpsInvoiced(state, sellerLabelProviderIds)
      : isAnyFulfillmentPlanIsUpsInvoiced(state, fulfillmentPlanIds ?? []);

    const needsToPickPaymentMethod =
      !selectPaymentMethodsHasActiveShipmentInvoicePayment(state) || !Global.selectSellerHasViewedUpsInvoicedModal(state);
    return isUpsInvoiced && needsToPickPaymentMethod;
  },
  launchModal: (onSuccess, onCancel) => {
    let didAccept = false;
    let modalId = '';
    const onClose = () => {
      if (!didAccept) {
        onCancel?.();
      }
    };
    const _onCancel = () => {
      onClose();
      modals.close(modalId);
    };
    const onAccept = () => {
      didAccept = true;
      modals.close(modalId);
      onSuccess();
    };
    modalId = modals.open({
      title: <FormattedMessage {...messages.title} />,
      size: 'md',
      zIndex: getMaxZ(),
      children: <UpsInvoicedNotificationModal onClose={_onCancel} onAccept={onAccept} />,
      onClose,
    });
    return modalId;
  },
};
